import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, ChevronDown, Check } from 'lucide-react';

export interface SortOption {
  id: string;
  label: string;
}

interface SortDropdownProps {
  options: SortOption[];
  selectedOptionId: string;
  onOptionSelect: (optionId: string) => void;
  className?: string;
}

export const SortDropdown: React.FC<SortDropdownProps> = ({
  options,
  selectedOptionId,
  onOptionSelect,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.id === selectedOptionId) || options[0];

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        className="flex items-center justify-between w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        onClick={toggleDropdown}
      >
        <div className="flex items-center">
          <ArrowUpDown className="w-4 h-4 mr-2 text-gray-500" />
          <span className="text-sm mr-1">
            {t('mediaGallery.sort.title')}:
          </span>
          <span className="text-sm font-medium">
            {t(selectedOption.label)}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-150 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.map((option) => (
            <button
              key={option.id}
              className={`flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 ${selectedOptionId === option.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
              onClick={() => {
                onOptionSelect(option.id);
                setIsOpen(false);
              }}
            >
              {selectedOptionId === option.id && (
                <Check className="w-4 h-4 mr-1 text-blue-600" />
              )}
              <span>{t(option.label)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}; 